<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <b-row>
        <b-col cols="12">
          <b-card>
            <h4>{{ 'audit_logs_list_title' | translate('global') }}</h4>
            <hr>
            <div v-if="spinners.contentIsLoading" class="text-center">
              <b-spinner type="grow" variant="primary"/>
            </div>
            <div v-else>
              <!--------FILTERS---------------------->
              <b-row>
                <b-col cols="6">
                  <b-form-group>
                    <treeselect :default-expand-level="1"
                                :placeholder="'select_creator' | translate('global')"
                                instanceId="loadUsers"
                                :load-options="loadOptions" :async="true" :cacheOptions="false"
                                :searchPromptText="'creator_autocomplete' | translate('global')"
                                :noResultsText="'no_results_found' | translate('global')"
                                v-model="filters.user"/>
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group>
                    <datepicker :placeholder="'select_date_range' | translate('global')"
                                v-model="filters.dateRange" range format="DD.MM.YYYY"/>
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group>
                    <treeselect :options="logNamesOptions" :default-expand-level="1"
                                :placeholder="'title_placeholder' | translate('global')"
                                v-model="filters.log_name"/>
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group>
                    <treeselect :options="descriptionsOptions" :default-expand-level="1"
                                :placeholder="'action_placeholder' | translate('global')"
                                v-model="filters.description"/>
                  </b-form-group>
                </b-col>

                <b-col cols="12" class="mb-2">
                  <b-button variant="primary" block @click="getData()"
                            :disabled="disabledButtons.filter">
                    {{ 'filter' | translate('global') }}
                  </b-button>
                </b-col>
              </b-row>
              <!--------END OF FILTERS--------------->

              <b-row>
                <b-col cols="12">
                  <EmptyTable v-if="!tableItems.length"/>
                  <b-table v-else class="table-sm text-center" responsive bordered
                           :fields="tableFields" :items="tableItems"/>
                </b-col>
              </b-row>
              <b-row>
                <!--Paginator-->
                <b-col cols="12" v-if="tableItems.length">
                  <Pagination :data="listPaginator" :limit="5" @pagination-change-page="onPageChange"/>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import statuses from '@/store/statuses';
import forEach from 'lodash.foreach';
import { mapGetters, mapActions } from 'vuex';
import EmptyTable from '@/views/sharedComponents/EmptyTableData';
import Pagination from 'laravel-vue-pagination';
import VuexTypes from '@/store/types/index';
import Services from '@/services/index';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import TreeselectMixin from '@/mixins/TreeselectMixin';
import Datepicker from 'vue2-datepicker';
import 'vue2-datepicker/locale/lv';

const AuditLogsTypes = VuexTypes.auditLogs;
const AuditLogsService = Services.auditLogs;
export default {
  mounted() {
    this.getData();
  },
  props: ['page'],
  mixins: [TreeselectMixin],
  data() {
    return {
      spinners: {
        contentIsLoading: true,
      },
      disabledButtons: {
        filter: false,
      },
      logNamesOptions: [],
      descriptionsOptions: [],
      filters: {
        user: null,
        log_name: null,
        description: null,
        dateRange: null,
      },
      tableFields: [
        { key: 'id', label: this.$t('id', 'global') },
        { key: 'log_name', label: this.$t('title', 'global') },
        { key: 'description', label: this.$t('description', 'global') },
        { key: 'causer', label: this.$t('causer', 'global') },
        { key: 'created_at', label: this.$t('created_at', 'global') }
      ],
      tableItems: []
    };
  },
  components: {
    Pagination, Treeselect, Datepicker, EmptyTable
  },
  computed: {
    ...mapGetters('AuditLogs', [
      'auditLogsList',
      'auditLogsListFilters'
    ]),
    params() {
      const data = {};
      forEach(this.filters, (value, param) => {
        if (value || value === 0) {
          if (param === 'dateRange') {
            data['created_at[from]'] = value[0];
            data['created_at[to]'] = value[1];
          } else {
            data[param] = value;
          }
        }
      });
      data['page'] = this.page;
      return data;
    },
    listPaginator() {
      return this.auditLogsList.data.meta;
    },
    list() {
      return this.auditLogsList;
    },
  },
  watch: {
    page: 'getData',
    listPaginator: {
      handler: function () {
        if (this.listPaginator && this.listPaginator.last_page < this.page) {
          this.onPageChange(this.listPaginator.last_page);
        }
      },
      deep: true
    },
    list: {
      handler: function () {
        this.disabledButtons.filter = false;
        if (this.list['status'] === statuses.LOADED) {
          this.fillTableData();
          this.spinners.contentIsLoading = false;
        }
      },
      deep: true
    },
    auditLogsListFilters: {
      handler: function () {
        if (this.auditLogsListFilters['status'] === statuses.LOADED) {
          this.fillFilterData();
        }
      },
      deep: true
    },
  },
  methods: {
    ...mapActions('AuditLogs', [
      AuditLogsTypes.AUDIT_LOGS_LIST,
      AuditLogsTypes.AUDIT_LOGS_LIST_FILTERS,
    ]),
    getData() {
      this.disabledButtons.filter = true;
      this.AUDIT_LOGS_LIST(this.params);
      this.AUDIT_LOGS_LIST_FILTERS();
    },
    fillTableData() {
      const result = [];
      forEach(this.list.data.data, (log) => {
        result.push({
          id: log['id'],
          log_name: log['log_name'],
          description: log['description'],
          causer: log['causer'] ? log['causer']['full_name'] : null,
          created_at: log['created_at']
        });
      });

      this.tableItems = result;
    },
    fillFilterData() {
      const logNamesResult = [];
      const descriptionsResult = [];
      const logNames = this.auditLogsListFilters.data['logNames'];
      const descriptions = this.auditLogsListFilters.data['descriptions'];

      forEach(logNames, (name) => {
        logNamesResult.push({
          id: name['log_name'],
          label: name['log_name'],
        });
      });

      forEach(descriptions, (description) => {
        descriptionsResult.push({
          id: description['description'],
          label: description['description'],
        });
      });

      this.logNamesOptions = logNamesResult;
      this.descriptionsOptions = descriptionsResult;
    },
    onPageChange(page) {
      if (!page) {
        page = this.page;
      }
      if (page !== this.page) {
        this.$router.push({ name: 'AuditLogs', params: { page } });
      }
    },
    async loadOptions({ action, searchQuery, callback, instanceId }) {
      let options = [];
      const query = searchQuery.trim();
      if (action === 'ASYNC_SEARCH') {
        const valid = this.checkSearchQuery(query);
        if (valid) {
          options = await this[instanceId](query);
        }
      }
      callback(null, options);
    },
    async loadUsers(name) {
      const param = name || '';
      const data = await AuditLogsService.searchUser(param);
      const options = [];
      forEach(data.data.data, (item) => {
        options.push({
          id: item['id'],
          label: item['full_name']
        });
      });
      return options;
    },
  }
};

</script>

<style scoped>
</style>
